import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import type { State } from "@/redux/Types";

type MiniTooltipProps = {
  text: string;
  position?: "top" | "bottom" | "left" | "right";
  bgColor?: string;
  maxWidth?: string;
  className?: string;
};

const MiniTooltip = ({
  text,
  position = "top",
  bgColor = "#222",
  maxWidth,
  className,
}: MiniTooltipProps) => {
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 2, left: 0 });
  const [showAnimation, setShowAnimation] = useState(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tooltipElement = tooltipRef.current;
    if (!tooltipElement) return;

    const parent = tooltipElement.parentElement;
    if (!parent) return;

    const handleMouseEnter = () => {
      const rect = parent.getBoundingClientRect();
      const offset = 12;

      const dummy = document.createElement("div");
      dummy.style.position = "fixed";
      dummy.style.visibility = "hidden";
      dummy.style.padding = "6px 10px";
      dummy.style.fontSize = "12px";
      if (maxWidth) {
        dummy.style.maxWidth = maxWidth;
        dummy.style.whiteSpace = "normal";
      } else {
        dummy.style.whiteSpace = "nowrap";
      }
      dummy.innerText = text;
      document.body.appendChild(dummy);
      const tooltipRect = dummy.getBoundingClientRect();
      document.body?.removeChild(dummy);

      const positions = {
        top: {
          top: rect.top - tooltipRect.height - offset,
          left: rect.left + rect.width / 2 - tooltipRect.width / 2,
        },
        bottom: {
          top: rect.bottom + offset - 4,
          left: rect.left + rect.width / 2 - tooltipRect.width / 2,
        },
        left: {
          top: rect.top + rect.height / 2 - tooltipRect.height / 2,
          left: rect.left - tooltipRect.width - offset + 5,
        },
        right: {
          top: rect.top + rect.height / 2 - tooltipRect.height / 2,
          left: rect.right + offset - 5,
        },
      };

      const margin = 8;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;
      const clamped = { ...positions[position] };
      clamped.left = Math.min(
        Math.max(clamped.left, margin),
        viewportWidth - tooltipRect.width - margin,
      );
      clamped.top = Math.min(
        Math.max(clamped.top, margin),
        viewportHeight - tooltipRect.height - margin,
      );

      setCoords(clamped);
      setVisible(true);

      setTimeout(() => setShowAnimation(true), 10);
    };

    const handleMouseLeave = () => {
      setShowAnimation(false);
      setVisible(false);
    };

    parent.addEventListener("mouseenter", handleMouseEnter);
    parent.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      parent.removeEventListener("mouseenter", handleMouseEnter);
      parent.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [text, position, maxWidth]);

  const caret = {
    top: (
      <div
        style={{
          position: "absolute",
          bottom: -5,
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderTop: `6px solid ${bgColor}`,
        }}
      />
    ),
    bottom: (
      <div
        style={{
          position: "absolute",
          top: -5,
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderBottom: `6px solid ${bgColor}`,
        }}
      />
    ),
    left: (
      <div
        style={{
          position: "absolute",
          right: -6,
          top: "50%",
          transform: "translateY(-50%)",
          width: 0,
          height: 0,
          borderTop: "6px solid transparent",
          borderBottom: "6px solid transparent",
          borderLeft: `6px solid ${bgColor}`,
        }}
      />
    ),
    right: (
      <div
        style={{
          position: "absolute",
          left: -6,
          top: "50%",
          transform: "translateY(-50%)",
          width: 0,
          height: 0,
          borderTop: "6px solid transparent",
          borderBottom: "6px solid transparent",
          borderRight: `6px solid ${bgColor}`,
        }}
      />
    ),
  };

  const getTransformOrigin = () => {
    switch (position) {
      case "top":
        return "bottom center";
      case "bottom":
        return "top center";
      case "left":
        return "center right";
      case "right":
        return "center left";
      default:
        return "top center";
    }
  };

  const getInitialTransform = () => {
    switch (position) {
      case "top":
        return "translateY(8px)";
      case "bottom":
        return "translateY(-8px)";
      case "left":
        return "translateX(8px)";
      case "right":
        return "translateX(-8px)";
      default:
        return "translateY(-8px)";
    }
  };

  return visible ? (
    createPortal(
      <div
        ref={tooltipRef}
        className={className}
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          backgroundColor: bgColor,
          color: "#fff",
          padding: "6px 10px",
          fontSize: "12px",
          borderRadius: "5px",
          whiteSpace: maxWidth ? "normal" : "nowrap",
          maxWidth: maxWidth,
          zIndex: 9999,
          pointerEvents: "none",
          ...(fromOutgrow && { fontFamily: '"Ubuntu", sans-serif' }),
          transition: "opacity 0.2s ease, transform 0.2s ease",
          opacity: showAnimation ? 1 : 0,
          transform: showAnimation ? "translate(0, 0)" : getInitialTransform(),
          transformOrigin: getTransformOrigin(),
        }}
      >
        {text}
        {caret[position]}
      </div>,
      document.body,
    )
  ) : (
    <div ref={tooltipRef} style={{ display: "none" }} />
  );
};

export default MiniTooltip;
